import React, { useMemo, useContext } from "react";
import { IHeaderParams } from "ag-grid-community";
import { AgGridReact, Grid, Card, Text, Flex, HelpIndicator } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { withClientContext } from "~/apollo/client";
import { useGetArenaQuery } from "~/graphql/mainHooks";
import { useGetPicrPositionDetailsQuery } from "~/graphql/supergraphHooks";
import { type GetComplianceCaseQuery } from "~/graphql/supergraphTypes";
import { ComplianceRulesContext } from "~/pages/Compliance/complianceRulesContext";
import { usePicrRevampFeatureFlag } from "~/pages/Compliance/flags";
import { toGlobalId } from "~/utils/graphql-helpers";
import { generateGpMainUrl } from "~/utils/url";
import { getInvestingProfileName } from "../../Profiles/utils";
import {
  PositionCellRenderer,
  PaymentBeneficiaryCellRenderer,
  ScreeningCellRenderer,
  RiskRatingCellRenderer,
} from "./cellRenderers";
import PaymentInstructionCellRenderer from "./cellRenderers/PaymentInstructionCellRenderer";
import { getColumnDefs } from "./columnDefs";
import { TOOLTIP_TEXT } from "./constants";
import {
  positionsTableContainer,
  nestedRow,
  tooltipContainer,
  headerContainer,
  sortIconContainer,
} from "./styles_vanilla.css";
import { PositionDataWithId } from "./types";

interface HeaderWithInfoProps extends IHeaderParams {
  displayName: string;
  tooltipContent?: string;
}

// Custom Header Component with Info Button
export const HeaderWithInfoComponent = (props: HeaderWithInfoProps) => {
  const { displayName, tooltipContent, column, setSort, enableSorting } = props;
  const [sortDirection, setSortDirection] = React.useState<"asc" | "desc" | null>(null);

  const onSortChanged = () => {
    const currentSort = column.getSort();
    setSortDirection(currentSort as "asc" | "desc" | null);
  };

  React.useEffect(() => {
    column.addEventListener("sortChanged", onSortChanged);
    return () => {
      column.removeEventListener("sortChanged", onSortChanged);
    };
  }, [column]);

  const onSortClicked = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!enableSorting) return;

    const nextSort = sortDirection === null ? "asc" : sortDirection === "asc" ? "desc" : null;
    setSort(nextSort, e.shiftKey);
    setSortDirection(nextSort);
  };

  return (
    <Flex
      className={headerContainer}
      alignItems="center"
      gap={8}
      onClick={onSortClicked}
      data-testid="header-container"
    >
      <Text variant="strong-md">{displayName}</Text>
      {tooltipContent && (
        <HelpIndicator id={`${displayName}-tooltip`} placement="top" content={tooltipContent}>
          <Icon type="info-circle" variant="outline" />
        </HelpIndicator>
      )}
      {enableSorting && (
        <div className={sortIconContainer}>
          {sortDirection === "asc" && <Icon type="arrow-up" />}
          {sortDirection === "desc" && <Icon type="arrow-down" />}
        </div>
      )}
    </Flex>
  );
};

// Tooltip Components
export const ScreeningTooltipRenderer = () => {
  return <div className={tooltipContainer}>{TOOLTIP_TEXT.SCREENING}</div>;
};

export const RiskRatingTooltipRenderer = () => {
  return <div className={tooltipContainer}>{TOOLTIP_TEXT.RISK_RATING}</div>;
};

interface PositionsTableProps {
  readonly?: boolean;
  complianceCase: GetComplianceCaseQuery["complianceCase"] | undefined;
  caseId: number;
}

const PositionsTable: React.FC<PositionsTableProps> = ({
  readonly,
  caseId,
  complianceCase,
}) => {
  const rules = useContext(ComplianceRulesContext);
  const picrRevampEnabled = usePicrRevampFeatureFlag();

  // Fetch PICR position details
  const { data: picrPositionData } = useGetPicrPositionDetailsQuery({
    variables: {
      caseId,
    },

    fetchPolicy: "network-only",
    skip: !caseId,
  });

  const externalArenaId = complianceCase?.externalArena?.objectId;
  const numericArenaId = externalArenaId ? parseInt(externalArenaId, 10) : null;
  const globalId = numericArenaId ? toGlobalId("ArenaObjectType", numericArenaId) : null;

  const { data: arenaData } = useGetArenaQuery({
    context: withClientContext("staff"),
    variables: { id: globalId || "" },
    skip: !globalId,
  });

  const gpMainUrl = arenaData?.arena?.secondLevelDomain
    ? generateGpMainUrl(arenaData.arena.secondLevelDomain)
    : undefined;

  const components = useMemo(
    () => ({
      positionCellRenderer: PositionCellRenderer,
      paymentBeneficiaryCellRenderer: PaymentBeneficiaryCellRenderer,
      screeningCellRenderer: ScreeningCellRenderer,
      riskRatingCellRenderer: RiskRatingCellRenderer,
      paymentInstructionCellRenderer: PaymentInstructionCellRenderer,
      screeningTooltipRenderer: ScreeningTooltipRenderer,
      riskRatingTooltipRenderer: RiskRatingTooltipRenderer,
      headerWithInfoComponent: HeaderWithInfoComponent,
    }),
    [],
  );

  // Transform the data for tree structure
  const transformedData = useMemo(() => {
    const result = [];
    let currentId = 1;

    const complianceCaseName = !complianceCase
      ? "--"
      : rules.usesProfiles
        ? getInvestingProfileName(complianceCase?.investingProfile)
        : complianceCase?.name;

    const externalArenaName = complianceCase?.externalArena?.name;

    const countryAlpha2code =
      complianceCase?.investingProfile?.addresses?.[0]?.country?.alpha2code;

    const proposedPaymentAccount = complianceCase?.paymentAccount;

    if (!picrPositionData?.picrPositionDetailsByCaseId) {
      return [];
    }

    const allLinkedProfiles = picrPositionData.picrPositionDetailsByCaseId.flatMap((item) => {
      const profiles = [];

      if (item.linkedOnboardingCase?.profiles) {
        profiles.push(...item.linkedOnboardingCase.profiles);
      }

      if (item.linkedOnboardingCase?.investingProfile) {
        profiles.push(item.linkedOnboardingCase.investingProfile);
      }

      return profiles;
    });

    for (const item of picrPositionData.picrPositionDetailsByCaseId) {
      const program = item.linkedOnboardingCase?.program?.name;

      if (item.successorCases && item.successorCases.length > 0) {
        // Add group row with generated ID
        const parentId = currentId++;
        result.push({
          ...item,
          id: parentId,
          complianceCaseName,
          externalArenaName,
          externalArenaId,
          gpMainUrl,
          countryAlpha2code,
          readonly,
          program,
          proposedPaymentAccount,
          allLinkedProfiles,
        });

        // Add child rows based on successorCases with generated IDs
        for (const successorCase of item.successorCases) {
          result.push({
            id: currentId++,
            caseId: successorCase.id,
            positionId: item.positionId,
            complianceCaseName,
            externalArenaName,
            externalArenaId,
            gpMainUrl,
            investmentEntity: {
              name: item.investmentEntity.name,
            },
            currentBankName: item.currentBankName,
            redactedBankAccountNumber: item.redactedBankAccountNumber,
            parentId: parentId,
            countryAlpha2code,
            readonly,
            program,
            proposedPaymentAccount,
            allLinkedProfiles,
          });
        }
      } else {
        // Add regular row with generated IDs
        result.push({
          ...item,
          id: currentId++,
          complianceCaseName,
          externalArenaName,
          externalArenaId,
          gpMainUrl,
          countryAlpha2code,
          readonly,
          program,
          proposedPaymentAccount,
          allLinkedProfiles,
        });
      }
    }

    return result;
  }, [
    complianceCase,
    rules.usesProfiles,
    picrPositionData,
    gpMainUrl,
    externalArenaId,
    readonly,
  ]);

  const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
    suppressCellFocus: true,
    rowHeight: 42,
    headerHeight: 48,
    autoSizeStrategy: {
      type: "fitGridWidth",
    },
    domLayout: "autoHeight",
    components,
    groupDisplayType: "groupRows",
    rowGroupPanelShow: "never",
    groupDefaultExpanded: 1,
    animateRows: true,
    getDataPath: (data: PositionDataWithId) => {
      const hasSuccessorCases = data.successorCases && data.successorCases.length > 0;
      if (hasSuccessorCases) {
        return [data.id.toString()];
      } else if (data.parentId) {
        return [data.parentId.toString(), data.id.toString()];
      } else {
        return [data.id.toString()];
      }
    },
    treeData: true,
    defaultColDef: {
      cellStyle: { display: "flex", alignItems: "center", padding: "0" },
    },
  };

  return (
    <Card>
      {picrRevampEnabled ? (
        <Text marginBottom="lg" variant="secondary-md">
          AFFECTED POSITIONS
        </Text>
      ) : (
        <Text marginBottom="lg" variant="strong-md">
          POSITIONS TO UPDATE
        </Text>
      )}
      <div className={positionsTableContainer}>
        <Grid
          gridOptions={gridOptions}
          columnDefs={getColumnDefs(picrRevampEnabled)}
          rowData={transformedData}
          suppressMovableColumns
          suppressColumnVirtualisation
          autoGroupColumnDef={{
            headerName: "Position",
            field: "complianceCaseName",
            cellRenderer: "agGroupCellRenderer",
            cellRendererParams: {
              innerRenderer: "positionCellRenderer",
              suppressCount: true,
            },
            flex: 1,
          }}
          getRowClass={(params) => {
            return params.data.parentId ? nestedRow : "";
          }}
        />
      </div>
    </Card>
  );
};

export default PositionsTable;
